/*
 * How a chat body draws what only chat means: the two mention tokens this module registers with the
 * server's parser (mention_tokens.go) — `#slug`, a conversation, and `@channel`, everybody in it —
 * and the permalink chip, whose address is a route of this module. Core's `MarkdownBody` draws
 * everything else and hands these to the drawings built here (`RenderContextValue.mentionKinds`,
 * `permalinkHref`).
 *
 * A `#slug` is the one node the server leaves for this end to finish (X-A4 N6). The render tree is one
 * cached row shared by every reader, and whether a short name points at something a reader may open
 * is a property of the reader — so the chip is decided here, against the reader's `slugs` projection,
 * and a name that is not in it stays the characters the author typed. That is the security rule as
 * much as the usability one: a pill for a private conversation somebody is not in would confirm that
 * it exists, which is what `visibility.go` refuses to do on the server.
 */

import type { MouseEvent } from "react"
import type { MentionDrawing, RenderContextValue } from "../../../core/render/context.ts"
import { safeHref } from "../../../routes/safeHref.ts"
import { t } from "../i18n/t.ts"
import { conversationPath } from "../navigation.ts"
import { MentionKindChannel, MentionKindConversation } from "../wire/chat.gen.ts"
import { permalinkTarget } from "./QuotedMessage.tsx"

/** What chat adds to a render context: its two tokens and its permalink addresses. */
export type ChatAddressing = Required<Pick<RenderContextValue, "mentionKinds" | "permalinkHref">>

/**
 * The conversations the reader may open, by short name — the `slugs` projection read backwards. It is
 * inverted once per projection and remembered against it rather than rebuilt per chip, because a
 * conversation draws hundreds of bodies and the projection is built once per view; a `WeakMap` means
 * the index goes away with the view it belongs to.
 */
const conversationsBySlug = new WeakMap<Record<number, string>, Map<string, number>>()

function conversationBySlug(slugs: Record<number, string>, slug: string): number | undefined {
	let index = conversationsBySlug.get(slugs)
	if (index === undefined) {
		index = new Map<string, number>()
		// A direct conversation carries no slug and stands in the projection as "", which is the one
		// value that names no conversation.
		for (const [id, known] of Object.entries(slugs)) if (known !== "") index.set(known, Number(id))
		conversationsBySlug.set(slugs, index)
	}
	return index.get(slug)
}

/**
 * A `#slug` reference: a link for a reader who has that conversation, and the characters the author
 * typed for a reader who has not. Inside a markdown link's label it is the characters too: the link
 * the author wrote is the door they meant, and an anchor may not hold another.
 */
function conversationChip(
	slug: string,
	insideLink: boolean,
	slugs: Record<number, string>,
	navigate: ((path: string) => void) | undefined,
) {
	const token = `#${slug}`
	const conv = insideLink ? undefined : conversationBySlug(slugs, slug)
	const href = conv === undefined ? null : safeHref(conversationPath(slug))
	if (href === null) return token
	return (
		<a
			className="tb-tag tb-mention tb-mention--conversation"
			href={href}
			onClick={(event: MouseEvent<HTMLAnchorElement>) => {
				// A modifier means "somewhere else": let the browser open its tab or window.
				if (!navigate || event.metaKey || event.ctrlKey || event.shiftKey) return
				event.preventDefault()
				navigate(href)
			}}
		>
			{token}
		</a>
	)
}

/** `@channel`: a token that names several people is outlined, like a group. */
const channelChip: MentionDrawing = (mention) => (
	<span
		className="tb-tag tb-tag--group tb-mention tb-mention--channel"
		title={t("chat.render.mentionChannel")}
	>
		{`@${mention.handle}`}
	</span>
)

/**
 * The drawings for one reader's projection. A surface builds them once per `slugs` and `navigate`
 * (renderContext.ts), because they are part of the render context's value and a new one re-renders
 * every body on the screen.
 *
 * A permalink links by the address the author wrote, for everybody: unlike a `#slug` it is an
 * address and not a name, so the router does the refusing (QuotedMessage.tsx's `permalinkTarget`).
 */
export function chatAddressing(
	slugs: Record<number, string>,
	navigate?: (path: string) => void,
): ChatAddressing {
	return {
		mentionKinds: {
			[MentionKindConversation]: (mention, { insideLink }) =>
				conversationChip(mention.handle, insideLink, slugs, navigate),
			[MentionKindChannel]: channelChip,
		},
		permalinkHref: (perma) => permalinkTarget(perma, slugs),
	}
}
